import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {VetClinicService} from './vet-clinic.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu Partnerów (Partners).
 * Weryfikuje po stronie serwera API, czy aktualnie zalogowany użytkownik
 * ma odpowiednie uprawnienia (RBAC).
 */
@Injectable({
  providedIn: 'root'
})
export class PartnersAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private vetClinicService: VetClinicService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu modułu partnerów.
   * Mechanizm pre-autoryzacji zapobiegający transferowi chronionej logiki biznesowej
   * do przeglądarki użytkownika nieposiadającego wystarczających uprawnień,
   * optymalizujący również obciążenie łącza sieciowego.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku pomyślnej weryfikacji serwerowej).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/partners-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      }),
      map(response => {
        return true;
      }));
  }

  /**
   * Weryfikuje uprawnienia przed aktywacją i wyrenderowaniem widoku panelu partnerów.
   * W przypadku wygaśnięcia sesji (HTTP 401) wymusza miękkie wylogowanie i przekierowanie,
   * a braki w uprawnieniach domenowych (np. 403 Forbidden) obsługuje poprzez delegację
   * powiadomienia (Toast) do serwisów z klasy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/partners-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
